import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import type { SiteNavItem } from "../../lib/siteNav";

type MobileNavProps = {
  open: boolean;
  onClose: () => void;
  items: SiteNavItem[];
};

export function MobileNav({ open, onClose, items }: MobileNavProps): JSX.Element {
  const { pathname } = useLocation();

  useEffect(() => {
    onClose();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-[60] lg:hidden transition-opacity duration-300",
        open ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none",
      )}
      aria-hidden={!open}
    >
      <div className="absolute inset-0 bg-wwwuxmichalcomdodger-blue flex flex-col">
        <div className="flex items-center justify-end h-16 px-4 shrink-0 border-b border-white/15">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-12 h-12 text-white/70 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-6 py-8">
          <ul className="space-y-1">
            {items.map((item) => (
              <li key={item.id}>
                <Link
                  to={item.href}
                  onClick={onClose}
                  className={cn(
                    "block py-3 text-3xl sm:text-4xl font-['Montserrat',Helvetica] font-semibold uppercase tracking-wider transition-colors",
                    pathname === item.href || pathname.startsWith(`${item.href}/`)
                      ? "text-white"
                      : "text-white/60 hover:text-white",
                  )}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <Button
            asChild
            className="mt-10 w-full bg-white text-wwwuxmichalcomdodger-blue hover:bg-blue-50 text-base font-semibold py-6 h-auto rounded-lg"
          >
            <Link to="/contact" onClick={onClose}>
              Start Project
            </Link>
          </Button>
        </nav>
      </div>
    </div>
  );
}
